/**
 * ReauthModal component for session expiration handling.
 *
 * Displays a modal when the user's session expires, offering options to
 * re-authenticate or dismiss (which may limit functionality).
 */

import { useTranslation } from 'react-i18next';
import { LogIn, ShieldAlert } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export interface ReauthModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when the user chooses to log in */
  onLogin: () => void;
  /** Callback when the user dismisses the modal */
  onDismiss: () => void;
  /** Whether login is in progress */
  isLoading?: boolean;
}

/**
 * Modal displayed when user session expires and re-authentication is required.
 *
 * Features:
 * - Clear explanation of session expiration
 * - Login button to redirect to authentication
 * - Dismiss option (with warning about limited functionality)
 * - Accessibility-friendly with proper focus management
 *
 * @example
 * <ReauthModal
 *   open={showReauthModal}
 *   onLogin={handleLogin}
 *   onDismiss={handleDismiss}
 * />
 */
export function ReauthModal({ open, onLogin, onDismiss, isLoading = false }: ReauthModalProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onDismiss();
      }}
    >
      <DialogContent
        className="glass-panel border-border/50 sm:max-w-[425px]"
        data-testid="reauth-modal"
      >
        <DialogHeader>
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10">
            <ShieldAlert className="h-6 w-6 text-amber-500" aria-hidden="true" />
          </div>
          <DialogTitle className="text-center">
            {t('auth.sessionExpired', 'Session Expired')}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t(
              'auth.sessionExpiredDescription',
              'Your session has expired for security reasons. Please log in again to continue using Andi.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-center text-foreground-muted">
            {t('auth.sessionExpiredNote', 'Your current page will be preserved after logging in.')}
          </p>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Button
            onClick={onLogin}
            disabled={isLoading}
            className="w-full sm:w-auto"
            data-testid="reauth-login-button"
          >
            <LogIn className="mr-2 h-4 w-4" aria-hidden="true" />
            {isLoading
              ? t('auth.loggingIn', 'Logging in...')
              : t('auth.loginAgain', 'Log In Again')}
          </Button>
          <Button
            variant="ghost"
            onClick={onDismiss}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {t('common.dismiss', 'Dismiss')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReauthModal;
